/**
 * b2b-registration.js
 * Multi-step B2B registration form controller.
 * Handles: step navigation, real-time VAT validation, form submission.
 *
 * Multi-instance architecture: the snippet b2b-registration-form.liquid is
 * rendered with an `instance` param at up to four sites (modal-wrapper,
 * b2b-info-page, b2b-registration, main-customers-register). All IDs that
 * carry the instance suffix; JS attaches per-form via `[data-b2b-form]` and
 * uses `name` + scoped data-attrs for lookups so multiple instances on the
 * same page (e.g. modal + account inline) work independently.
 */
(function () {
  'use strict';

  var cfg = window.B2B_CONFIG || {};
  var isDE = cfg.isDE || false;

  // The App Proxy is mounted at the shop root and answers on BOTH /apps/reco/b2b and
  // /<locale>/apps/reco/b2b (both measured 200). What it does NOT answer is a value
  // where the locale ran into the path without a separator: four Liquid sites used to
  // emit `{{ routes.root_url | append: "apps/reco/b2b" }}`, and root_url carries no
  // trailing slash outside a market's primary locale — so every /en, /da, /fr visitor
  // got "/enapps/reco/b2b" and a 404 from Shopify. B2B registration was dead on every
  // non-primary locale for months, and the request never reached the backend, so
  // nothing showed up in the logs.
  //
  // The Liquid is fixed, but pages already in a browser or CDN cache still carry the
  // broken value. This check is deliberately DISCRIMINATING rather than a blanket
  // "must start with /apps/": it accepts any path ENDING in /apps/reco/b2b, so the
  // legitimate locale-prefixed form keeps working and only the mangled one is
  // replaced. A blanket rule would silently discard a valid configuration.
  var VALID_PROXY_PATH = /(^|\/)apps\/reco\/b2b$/;
  var apiBase = cfg.apiBase || '/apps/reco/b2b';
  if (!VALID_PROXY_PATH.test(apiBase)) {
    if (window.console && console.warn) {
      console.warn('[b2b] ignoring malformed apiBase %s — falling back to /apps/reco/b2b', apiBase);
    }
    // console.warn alone was the whole problem last time: the 404 happened at Shopify's
    // edge, produced no log line anywhere, and went unnoticed for three and a half
    // months. Emit an event too, so anything listening on the page can report it.
    window.dispatchEvent(new CustomEvent('b2b:config-error', {
      detail: { reason: 'malformed_api_base', value: apiBase, fallback: '/apps/reco/b2b' }
    }));
    apiBase = '/apps/reco/b2b';
  }

  function t(key) {
    var strings = {
      validating: isDE ? '⏳ Prüfung läuft…' : '⏳ Validating…',
      valid: isDE ? '✓ Gültige USt-IdNr.' : '✓ Valid VAT Number',
      invalid: isDE ? '✗ Ungültige USt-IdNr.' : '✗ Invalid VAT Number',
      serviceError: isDE ? '⚠ Prüfservice nicht verfügbar — wird manuell geprüft' : '⚠ Validation service unavailable — will be reviewed manually',
      formatError: isDE ? '✗ Ungültiges Format' : '✗ Invalid format',
      required: isDE ? 'Pflichtfeld' : 'This field is required',
      emailInvalid: isDE ? 'Bitte gültige E-Mail eingeben' : 'Please enter a valid email',
      phoneInvalid: isDE ? 'Bitte Telefonnummer eingeben' : 'Please enter a phone number',
      vatRequired: isDE ? 'Bitte USt-IdNr. eingeben' : 'Please enter your VAT number',
      termsRequired: isDE ? 'Bitte alle Pflichtfelder akzeptieren' : 'Please accept all required terms',
      submitError: isDE ? 'Fehler beim Absenden. Bitte versuchen Sie es erneut.' : 'Submission failed. Please try again.',
      submitting: isDE ? 'Wird gesendet…' : 'Submitting…',
    };
    return strings[key] || key;
  }

  function showEl(el) { if (el) el.style.display = ''; }
  function hideEl(el) { if (el) el.style.display = 'none'; }

  // ---------------------------------------------------------------------------
  // Per-form controller
  // ---------------------------------------------------------------------------
  function initForm(form) {
    if (!form || form.dataset.b2bInit === 'true') return;
    form.dataset.b2bInit = 'true';

    var wrap = form.closest('[data-b2b-reg-wrap]') || form.parentElement;
    var instance = form.dataset.instance || 'global';

    function f(sel) { return form.querySelector(sel); }
    function fAll(sel) { return Array.from(form.querySelectorAll(sel)); }
    function w(sel) { return wrap.querySelector(sel); }

    var steps = fAll('.b2b-form-step');
    var stepIndicators = wrap ? Array.from(wrap.querySelectorAll('.b2b-step-ind')) : [];
    var successPanel = w('[data-b2b-success]');
    var currentStep = 1;

    // -------------------------------------------------------------------------
    // Step navigation
    // -------------------------------------------------------------------------
    // Bring the form (or, after submit, the success panel) into view WITHOUT
    // jumping to the top of the page. In a modal: scroll the modal panel.
    // On a page: scroll to the form anchor offset by the sticky header.
    function scrollToForm() {
      var modalPanel = form.closest('.b2b-modal__panel');
      if (modalPanel) {
        modalPanel.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      var anchor = document.getElementById('b2bInlineFormAnchor') || wrap;
      if (anchor) {
        var hh = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '70', 10);
        var y = anchor.getBoundingClientRect().top + window.scrollY - hh - 12;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }

    function goToStep(n) {
      currentStep = n;
      steps.forEach(function (s) {
        var stepNum = parseInt(s.dataset.step, 10);
        s.classList.toggle('active', stepNum === n);
      });
      stepIndicators.forEach(function (ind) {
        var stepNum = parseInt(ind.dataset.step, 10);
        ind.classList.remove('active', 'done');
        if (stepNum === n) ind.classList.add('active');
        else if (stepNum < n) ind.classList.add('done');
      });

      scrollToForm();

      window.dispatchEvent(new CustomEvent('b2b:step', {
        detail: { step: n, total: steps.length, instance: instance }
      }));
    }

    // -------------------------------------------------------------------------
    // Field validation helpers
    // -------------------------------------------------------------------------
    function setError(input, msg) {
      if (!input) return;
      input.classList.add('error');
      input.setAttribute('aria-invalid', 'true');
      var errEl = input.parentElement && input.parentElement.querySelector('.b2b-field-error');
      if (errEl) errEl.textContent = msg;
    }

    function clearError(input) {
      if (!input) return;
      input.classList.remove('error');
      input.removeAttribute('aria-invalid');
      var errEl = input.parentElement && input.parentElement.querySelector('.b2b-field-error');
      if (errEl) errEl.textContent = '';
    }

    function validateRequired(input, minLen) {
      if (!input) return false;
      var val = input.value.trim();
      if (!val || (minLen && val.length < minLen)) {
        setError(input, t('required'));
        return false;
      }
      clearError(input);
      return true;
    }

    function validateEmail(input) {
      if (!input) return false;
      var val = input.value.trim();
      if (!val || !val.includes('@') || !val.includes('.')) {
        setError(input, t('emailInvalid'));
        return false;
      }
      clearError(input);
      return true;
    }

    function validateStep1() {
      var ok = true;
      ok = validateRequired(f('[name="company_name"]'), 2) && ok;
      ok = validateRequired(f('[name="vat_number"]'), 4) && ok;
      ok = validateRequired(f('[name="contact_person_name"]'), 2) && ok;
      ok = validateEmail(f('[name="company_email"]')) && ok;
      ok = validateRequired(f('[name="company_phone"]'), 4) && ok;

      var vatStatus = w('[data-b2b-vat-status]');
      if (vatStatus && vatStatus.classList.contains('invalid')) {
        var vatInput = f('[name="vat_number"]');
        setError(vatInput, isDE ? 'USt-IdNr. ungültig' : 'VAT number is invalid');
        ok = false;
      }
      return ok;
    }

    function validateStep2() {
      var ok = true;
      ok = validateRequired(f('[name="billing_street"]'), 5) && ok;
      ok = validateRequired(f('[name="billing_city"]'), 2) && ok;
      ok = validateRequired(f('[name="billing_postal_code"]'), 3) && ok;
      var country = f('[name="billing_country"]');
      if (country && !country.value) {
        setError(country, t('required'));
        ok = false;
      } else if (country) {
        clearError(country);
      }

      var shippingSame = f('[data-b2b-shipping-same]');
      if (shippingSame && !shippingSame.checked) {
        ok = validateRequired(f('[name="shipping_street"]'), 5) && ok;
        ok = validateRequired(f('[name="shipping_city"]'), 2) && ok;
        ok = validateRequired(f('[name="shipping_postal_code"]'), 3) && ok;
      }
      return ok;
    }

    function validateStep3() {
      var ok = true;
      var accuracy = f('[data-b2b-terms-accuracy]');
      var accepted = f('[data-b2b-terms-accepted]');
      if (!accuracy || !accepted || !accuracy.checked || !accepted.checked) {
        ok = false;
        showFormError(t('termsRequired'));
      }
      return ok;
    }

    // -------------------------------------------------------------------------
    // Step 3 — Summary generation
    // -------------------------------------------------------------------------
    function buildSummary() {
      var data = collectFormData();
      var rows = [];

      function row(label, val) {
        if (val) rows.push('<div class="b2b-summary-row"><span class="b2b-summary-label">' + label + '</span><span>' + val + '</span></div>');
      }

      row(isDE ? 'Firma' : 'Company', data.company_name);
      row('USt-IdNr. / VAT', data.vat_number);
      row(isDE ? 'Ansprechpartner' : 'Contact', data.contact_person_name + (data.contact_person_position ? ' (' + data.contact_person_position + ')' : ''));
      row('Email', data.company_email);
      row(isDE ? 'Telefon' : 'Phone', data.company_phone);
      row(isDE ? 'Rechnungsadresse' : 'Billing', [data.billing_street, data.billing_city, data.billing_postal_code, data.billing_country].filter(Boolean).join(', '));
      if (!data.shipping_same_as_billing) {
        row(isDE ? 'Lieferadresse' : 'Shipping', [data.shipping_street, data.shipping_city, data.shipping_postal_code, data.shipping_country].filter(Boolean).join(', '));
      } else {
        row(isDE ? 'Lieferadresse' : 'Shipping', isDE ? 'Wie Rechnungsadresse' : 'Same as billing');
      }

      var container = w('[data-b2b-summary-content]');
      if (container) container.innerHTML = rows.join('');
    }

    // -------------------------------------------------------------------------
    // Collect form data
    // -------------------------------------------------------------------------
    function collectFormData() {
      var fd = new FormData(form);
      var data = {};
      fd.forEach(function (val, key) { data[key] = val; });

      var shippingSame = f('[data-b2b-shipping-same]');
      var accepted = f('[data-b2b-terms-accepted]');
      var marketing = f('[data-b2b-marketing]');
      data.shipping_same_as_billing = !!(shippingSame && shippingSame.checked);
      data.terms_accepted = !!(accepted && accepted.checked);
      data.marketing_consent = !!(marketing && marketing.checked);

      // The backend picks the language of its 422 sentence from this. Without it every
      // customer got the English sentence — including a German one on brewout.de looking
      // at an otherwise fully German form.
      //
      // isDE FIRST, and not cfg.locale alone: the snippet sets is_de from
      // `... or request.host contains '.de'`, so brewout.de/en renders a German form
      // while the locale iso_code is 'en'. isDE is what the rest of this file localizes
      // against (see the duplicate_vat branch below), so the message must follow it.
      data.locale = cfg.isDE ? 'de' : (cfg.locale || '');

      return data;
    }

    // -------------------------------------------------------------------------
    // VAT Validation
    // -------------------------------------------------------------------------
    var vatTimeout = null;
    var lastValidatedVat = '';

    function updateVatStatus(result) {
      var statusEl = w('[data-b2b-vat-status]');
      if (!statusEl) return;
      statusEl.className = 'b2b-vat-status';
      statusEl.style.display = '';

      if (result.status === 'valid') {
        statusEl.classList.add('valid');
        var company = result.company_name ? '\n' + result.company_name : '';
        var addr = result.company_address ? '\n' + result.company_address : '';
        statusEl.textContent = t('valid') + company + addr;
      } else if (result.status === 'invalid' || result.status === 'format_error') {
        statusEl.classList.add('invalid');
        statusEl.textContent = (result.status === 'format_error' ? t('formatError') : t('invalid')) + (result.error ? ': ' + result.error : '');
      } else if (result.status === 'service_error') {
        statusEl.classList.add('error');
        statusEl.textContent = t('serviceError');
      }
    }

    function validateVat(vatNumber) {
      if (!vatNumber || vatNumber.length < 5) return;
      lastValidatedVat = vatNumber;

      var statusEl = w('[data-b2b-vat-status]');
      if (statusEl) {
        statusEl.style.display = '';
        statusEl.className = 'b2b-vat-status loading';
        statusEl.textContent = t('validating');
      }

      fetch(apiBase + '/validate-vat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vat_number: vatNumber }),
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (vatNumber === lastValidatedVat) updateVatStatus(data);
        })
        .catch(function () {
          if (vatNumber === lastValidatedVat) {
            updateVatStatus({ status: 'service_error' });
          }
        });
    }

    var vatInput = f('[name="vat_number"]');
    if (vatInput) {
      vatInput.addEventListener('input', function () {
        clearTimeout(vatTimeout);
        vatTimeout = setTimeout(function () { validateVat(vatInput.value.trim()); }, 700);
      });
    }

    var validateBtn = f('[data-b2b-validate-vat]');
    if (validateBtn) {
      validateBtn.addEventListener('click', function () {
        clearTimeout(vatTimeout);
        var vi = f('[name="vat_number"]');
        validateVat((vi && vi.value) || '');
      });
    }

    // -------------------------------------------------------------------------
    // Form error display
    // -------------------------------------------------------------------------
    function showFormError(msg) {
      var errWrap = f('[data-b2b-form-error]');
      var errMsg = f('[data-b2b-form-error-msg]');
      if (errWrap) showEl(errWrap);
      if (errMsg) errMsg.textContent = msg;
    }

    function clearFormError() {
      var errWrap = f('[data-b2b-form-error]');
      if (errWrap) hideEl(errWrap);
    }

    // -------------------------------------------------------------------------
    // Shipping fields toggle
    // -------------------------------------------------------------------------
    var shippingSame = f('[data-b2b-shipping-same]');
    var shippingFields = f('[data-b2b-shipping-fields]');

    if (shippingSame && shippingFields) {
      shippingSame.addEventListener('change', function () {
        shippingFields.style.display = this.checked ? 'none' : '';
      });
    }

    // -------------------------------------------------------------------------
    // Navigation buttons (delegated via data-b2b-step-next/-back)
    // -------------------------------------------------------------------------
    fAll('[data-b2b-step-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(btn.dataset.b2bStepNext, 10);
        if (target === 2) {
          if (validateStep1()) goToStep(2);
        } else if (target === 3) {
          if (validateStep2()) { buildSummary(); goToStep(3); }
        }
      });
    });

    fAll('[data-b2b-step-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(btn.dataset.b2bStepBack, 10);
        if (target >= 1 && target <= 3) goToStep(target);
      });
    });

    // -------------------------------------------------------------------------
    // Form submission
    // -------------------------------------------------------------------------
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearFormError();

      if (!validateStep3()) return;

      var submitBtn = f('[data-b2b-submit]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="b2b-spinner"></span>' + t('submitting');
      }

      var data = collectFormData();

      fetch(apiBase + '/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then(function (r) {
          return r.json().then(function (body) { return { ok: r.ok, status: r.status, body: body }; });
        })
        .then(function (res) {
          if (res.ok && res.body.success) {
            hideEl(form);
            showEl(successPanel);
            window.dispatchEvent(new CustomEvent('b2b:step', {
              detail: { step: steps.length, total: steps.length, complete: true, instance: instance }
            }));
            // Keep the success panel in view instead of jumping to page top.
            scrollToForm();
          } else {
            var details = (res.body && res.body.details) || {};
            Object.keys(details).forEach(function (field) {
              var input = f('[name="' + field + '"]');
              if (input) setError(input, details[field]);
            });

            var msg = (res.body && (res.body.message || res.body.error)) || t('submitError');
            if (res.body && res.body.error === 'duplicate_vat') {
              msg = isDE
                ? 'Ein B2B-Konto mit dieser USt-IdNr. existiert bereits.'
                : 'A B2B account with this VAT number already exists.';
            }
            showFormError(msg);

            // Jump to the LOWEST step holding a rejected field, and put the cursor on
            // that field.
            //
            // Two traps this avoids. (a) The previous version listed five field names
            // explicitly, so an error on any of the other seven (contact_person_name,
            // billing_postal_code, billing_country, shipping_*) was written into a step
            // the visitor could not see, and the form appeared to refuse for no reason.
            // Deriving the step from the DOM cannot fall behind the field list.
            // (b) Picking `Object.keys(details)[0]` would be Flask's key order, not the
            // validation order: DefaultJSONProvider sorts keys, so an empty form reports
            // `billing_city` first and would throw the visitor to step 2 while step 1 is
            // also full of errors — contradicting the summary sentence, which is built in
            // validation order and opens with the company name. Lowest step is
            // order-independent and is where the visitor has to start anyway.
            var lowestStep = null;
            var firstBadInput = null;
            Object.keys(details).forEach(function (field) {
              var el = f('[name="' + field + '"]');
              var stepEl = el && el.closest('.b2b-form-step');
              var n = stepEl && parseInt(stepEl.dataset.step, 10);
              if (n && (lowestStep === null || n < lowestStep)) {
                lowestStep = n;
                firstBadInput = el;
              }
            });
            if (lowestStep && lowestStep !== currentStep) goToStep(lowestStep);
            // Focus after the step switch, so the field is actually visible. Screen
            // readers get the field; the summary carries role="alert" separately.
            if (firstBadInput && typeof firstBadInput.focus === 'function') {
              try { firstBadInput.focus({ preventScroll: true }); } catch (e) { firstBadInput.focus(); }
            }
          }
        })
        .catch(function () {
          showFormError(t('submitError'));
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = isDE ? 'Registrierung absenden' : 'Submit Registration';
          }
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Boot: initialise every B2B form on the page (idempotent — guarded per-form)
  // ---------------------------------------------------------------------------
  function bootAll() {
    document.querySelectorAll('[data-b2b-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

})();
